import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calculator, Sparkles, Clock, ShieldCheck, ArrowRight } from "lucide-react";

export default function CalculatorPromo() {
  return (
    <section
      id="estimateur"
      data-testid="calculator-section"
      className="relative py-24 md:py-32 bg-[#FAFAFA] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          {/* Left: Pitch */}
          <div className="md:col-span-6">
            <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-5">
              Nouveau · Exclusif
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-light tracking-tighter leading-[1.02]">
              Votre estimation.<br />
              <span className="text-[#FF2800]">En 2 minutes.</span>
            </h2>
            <p className="mt-8 text-lg text-neutral-600 max-w-lg leading-relaxed">
              Première entreprise du Grand Est à proposer une <span className="text-black font-semibold">estimation de prix instantanée</span>.
              Répondez au questionnaire, recevez votre estimation à l&apos;écran — puis votre devis officiel détaillé sous 24h.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/devis"
                data-testid="calculator-cta"
                className="btn-red inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm font-semibold"
              >
                Lancer mon estimation gratuite
                <ArrowRight size={16} />
              </Link>
              <div className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                100% Gratuit · Sans engagement
              </div>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 border-t border-black/10 pt-8">
              {[
                { icon: Clock, k: "2 min", v: "Temps moyen" },
                { icon: Sparkles, k: "Instant", v: "Prix affiché" },
                { icon: ShieldCheck, k: "0€", v: "Sans engagement" },
              ].map(({ icon: Icon, k, v }) => (
                <div key={k}>
                  <Icon size={18} className="text-[#FF2800] mb-3" strokeWidth={1.5} />
                  <div className="font-display text-xl md:text-2xl font-medium tracking-tight">{k}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 mt-1">{v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Mock estimator card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="md:col-span-6"
          >
            <div className="relative">
              {/* Yellow accent */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#FFB800] rounded-full blur-3xl opacity-30" />

              <div className="relative bg-black text-white rounded-3xl p-8 md:p-10 border border-white/10 shadow-2xl">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#FF2800]/20 border border-[#FF2800]/40 flex items-center justify-center">
                      <Calculator size={18} className="text-[#FFB800]" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.25em] text-neutral-400">D.E.S Estimateur</div>
                      <div className="text-sm text-white font-medium">Aperçu live</div>
                    </div>
                  </div>
                  <motion.div
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="text-[10px] uppercase tracking-[0.2em] text-[#FF2800] font-semibold"
                  >
                    ● Calcul actif
                  </motion.div>
                </div>

                <div className="space-y-3 mb-8">
                  <Row label="Volume" value="20 m³" />
                  <Row label="Distance" value="42 km" />
                  <Row label="Étages sans ascenseur" value="2" />
                  <Row label="Manutention" value="Standard" />
                </div>

                <div className="border-t border-white/10 pt-6">
                  <div className="text-xs uppercase tracking-[0.3em] text-[#FFB800] mb-3">Votre estimation</div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-sm text-neutral-400">à partir de</span>
                    <motion.span
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.4 }}
                      className="font-display text-5xl md:text-6xl font-light tracking-tighter text-[#FFB800]"
                    >
                      490 €
                    </motion.span>
                  </div>
                  <div className="text-xs text-neutral-500 mt-3 uppercase tracking-[0.2em]">
                    HT · Indicatif
                  </div>
                </div>
              </div>

              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="absolute -bottom-5 -left-5 bg-[#FF2800] text-white px-5 py-3 rounded-full text-xs font-semibold uppercase tracking-[0.2em] shadow-xl"
              >
                Exemple — Votre estimation en 2 min
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5">
      <span className="text-sm text-neutral-400">{label}</span>
      <span className="text-sm text-white font-medium">{value}</span>
    </div>
  );
}
